import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import ChatMediaProvider from "./ChatMediaProvider";
import {
  ConversationMediaList,
  MediaForwardList,
} from "../../../../model/chats/ChatMediaModel";
import { ConversationMediaListGroup } from "../../../../model/chats/ChatMediaGroupModel";
import EncryptDecryptService from "../../../../service/encrypt_decrypt/EncryptDecryptService";
import CacheStorage from "../../../../service/storage/CacheStorage";
import { isMobile } from "react-device-detect";
import { useDispatch } from "react-redux";
import {
  mediaDeleteEveryoneAction,
  setForwardMediaList,
} from "../../../../libraries/state_reducers/chats/ChatsReducer";
import {
  addModal,
  removeModal,
} from "../../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../../const/ModalKeys";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";

type ChatMediaContextType = {
  tabList: Array<"2" | "3" | "4" | "6" | "9" | "5">;
  selectedTab: "2" | "3" | "4" | "6" | "9" | "5";
  chatSelected: ConversationList | null;
  handleSend: () => void;
  handleDelete: () => void;
  handleClickTab: (id: "2" | "3" | "4" | "6" | "9" | "5") => void;

  medialListChucked:
    | Record<string, Record<string, ConversationMediaList[]>>
    | Record<string, Record<string, ConversationMediaListGroup[]>>
    | null;

  mediaIdsSelected: Array<string>;
  longPressStatus: boolean;
  handleClickMedia: (msgId: string) => void;
  handlePressStart: (msgId: string) => void;
  handlePressEnd: () => void;
  clearLongPress: () => void;
};

const ChatMediaContext = createContext<ChatMediaContextType | undefined>(
  undefined
);

export const ChatMediaContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const mediaDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteActionType
  );

  const forwardedMediaListSelector = useSelector(
    (state: RootState) => state.chats.forwardedMediaList
  );

  const userId = CacheStorage.getValue(StorageKeys.account_id) || "0";
  const chatSelectedRef = useRef<ConversationList | null>(null);

  let mediaList = useRef<Array<ConversationMediaList>>([]);
  let mediaListGroup = useRef<Array<ConversationMediaList>>([]);

  const messagesListChuckTypeGroupRef = useRef<
    | Record<string, Record<string, ConversationMediaList[]>>
    | Record<string, Record<string, ConversationMediaListGroup[]>>
    | null
  >(null);

  const privateKeyRef = useRef<string>(
    CacheStorage.getValue(StorageKeys.private_key) || ""
  );

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressStatusRef = useRef<boolean>(false);
  const mediaIdsSelectedRef = useRef<Array<string>>([]);

  const [chatSelected, setChatSelected] = useState<ConversationList | null>(
    null
  );

  const [mediaIdsSelected, setMediaIdsSelected] = useState<Array<string>>([]);
  const [longPressStatus, setLongPressStatus] = useState<boolean>(false);

  const [medialListChucked, setMedialListChucked] = useState<
    | Record<string, Record<string, ConversationMediaList[]>>
    | Record<string, Record<string, ConversationMediaListGroup[]>>
    | null
  >(null);

  const messagesTypes: Array<"2" | "3" | "4" | "6" | "7" | "9" | "5"> = [
    "2",
    "3",
    "4",
    "6",
    "7",
    "9",
    "5",
  ];

  const tabList: Array<"2" | "3" | "4" | "6" | "9" | "5"> = [
    "2",
    "3",
    "4",
    "6",
    "9",
    "5",
  ];
  const [selectedTab, setSelectedTab] = useState<
    "2" | "3" | "4" | "6" | "9" | "5"
  >("2");

  const reset = () => {
    chatSelectedRef.current = null;
    messagesListChuckTypeGroupRef.current = null;

    mediaList.current = [];
    mediaListGroup.current = [];

    setChatSelected(null);
    setSelectedTab("2");

    clearLongPress();
  };

  const clearLongPress = () => {
    longPressStatusRef.current = false;
    setLongPressStatus(longPressStatusRef.current);

    mediaIdsSelectedRef.current = [];
    setMediaIdsSelected(mediaIdsSelectedRef.current);
  };

  const handlePressStart = (msgId: string) => {
    timerRef.current = setTimeout(() => {
      longPressStatusRef.current = true;
      setLongPressStatus(true);
      console.log("Long press detected!");

      if (isMobile) {
        toggleMessageIdSelection(msgId);
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    console.log("Press and hold");
  };

  const handleClickMedia = (msgId: string) => {
    console.log(`handleClickMedia: ${longPressStatusRef.current}`);
    if (longPressStatusRef.current) {
      toggleMessageIdSelection(msgId);

      if (mediaIdsSelectedRef.current.length === 0) {
        clearLongPress();
      }
    }
  };

  const toggleMessageIdSelection = (msgId: string) => {
    let ids = [...mediaIdsSelectedRef.current];

    if (ids.includes(msgId)) {
      ids = ids.filter((item) => item !== msgId);
    } else {
      ids.push(msgId);
    }

    mediaIdsSelectedRef.current = ids;
    setMediaIdsSelected(ids);
  };

  const getSelectedChatSession = () => {
    const chatsChatSelectedSession = SessionStorage.getValue(
      StorageKeys.chats_chat_selected
    );

    if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
      const chatSelected: ConversationList = JSON.parse(
        chatsChatSelectedSession
      );

      chatSelectedRef.current = chatSelected;
      setChatSelected(chatSelected);
    }
  };

  const getMediaList = async () => {
    if (chatSelectedRef.current != null) {
      const flag: string = chatSelectedRef.current.flag!;

      const mediaListResult:
        | Array<ConversationMediaList>
        | Array<ConversationMediaListGroup>
        | null = await ChatMediaProvider.getMediaAPI(
        chatSelectedRef.current.conv_id!,
        flag === "4" ? "1" : flag
      );

      mediaList.current = [];
      mediaListGroup.current = [];

      if (mediaListResult != null) {
        if (["2", "3"].includes(flag)) {
          privateKeyRef.current =
            SessionStorage.getValue(StorageKeys.chats_symetric_key) || "";

          const mediaListResultGroup: Array<ConversationMediaListGroup> =
            mediaListResult;

          await Promise.all(
            mediaListResultGroup!.map(async (element, index) => {
              if (
                element?.message! != null &&
                UtilityHelper.isNotEmpty(element.message!)
              ) {
                element.decripted_mesage =
                  await EncryptDecryptService.decryptGroup({
                    secretKey: privateKeyRef.current,
                    cipherTextBase64: element.message!,
                    ivBase64: element.message_iv!,
                    tagBase64: element.message_tag!,
                  });
              }

              mediaListGroup.current.push(element);
            })
          );

          mediaListGroup.current = [...mediaListGroup.current].sort(
            (a, b) => Number(b.message_id) - Number(a.message_id)
          );

          let messagesListChuckType: Record<
            string,
            ConversationMediaListGroup[]
          > = ChatMediaProvider.groupByTypeDeptGroup(mediaListGroup.current);

          messagesListChuckType = Object.fromEntries(
            Object.entries(messagesListChuckType).reverse()
          );

          messagesListChuckTypeGroupRef.current = null;

          const messagesListChuckTypeGroup: Record<
            string,
            Record<string, ConversationMediaListGroup[]>
          > = {};

          if (Object.keys(messagesListChuckType["7"] ?? {}).length > 0) {
            if (Object.keys(messagesListChuckType["6"] ?? {}).length > 0) {
              messagesListChuckType["6"] = {
                ...messagesListChuckType["6"],
                ...messagesListChuckType["7"],
              };
            } else {
              messagesListChuckType["6"] = [...messagesListChuckType["7"]];
            }
          }

          Object.entries(messagesListChuckType).forEach(([msgType, group]) => {
            if (!["7"].includes(msgType)) {
              let dateGrouping: Record<string, ConversationMediaListGroup[]> =
                ChatMediaProvider.groupByDateDeptGroup(group);

              dateGrouping = Object.fromEntries(
                Object.entries(dateGrouping).reverse()
              );
              messagesListChuckTypeGroup[msgType] = dateGrouping;
            }
          });

          messagesListChuckTypeGroupRef.current = messagesListChuckTypeGroup;
          setMedialListChucked(messagesListChuckTypeGroupRef.current);
        } else {
          const mediaListResultTeam: Array<ConversationMediaList> =
            mediaListResult;

          mediaListResultTeam!.map(async (element, index) => {
            if (
              element?.message! != null &&
              messagesTypes.includes(
                element?.message_type as "2" | "3" | "4" | "6" | "7" | "9" | "5"
              )
            ) {
              element.decripted_mesage = UtilityHelper.isStringMap(
                element?.message!
              )
                ? element?.message_encryption_type === "e2e"
                  ? EncryptDecryptService.decryptWithPublicKey(
                      element.message![userId]!,
                      privateKeyRef.current
                    )
                  : ""
                : element?.message_encryption_type === "hybrid"
                ? EncryptDecryptService.hybridDecrypt(
                    element.message![userId]!,
                    privateKeyRef.current
                  )
                : "";
            }

            if (element.decripted_mesage != null) {
              mediaList.current.push(element);
            }
          });

          mediaList.current = [...mediaList.current].sort(
            (a, b) => Number(b.message_id) - Number(a.message_id)
          );

          let messagesListChuckType: Record<string, ConversationMediaList[]> =
            ChatMediaProvider.groupByType(mediaList.current);

          messagesListChuckType = Object.fromEntries(
            Object.entries(messagesListChuckType).reverse()
          );

          messagesListChuckTypeGroupRef.current = null;

          const messagesListChuckTypeGroup: Record<
            string,
            Record<string, ConversationMediaList[]>
          > = {};

          if (Object.keys(messagesListChuckType["7"] ?? {}).length > 0) {
            if (Object.keys(messagesListChuckType["6"] ?? {}).length > 0) {
              messagesListChuckType["6"] = {
                ...messagesListChuckType["6"],
                ...messagesListChuckType["7"],
              };
            } else {
              messagesListChuckType["6"] = [...messagesListChuckType["7"]];
            }
          }

          Object.entries(messagesListChuckType).forEach(([msgType, group]) => {
            if (!["7"].includes(msgType)) {
              let dateGrouping: Record<string, ConversationMediaList[]> =
                ChatMediaProvider.groupByDate(group);

              dateGrouping = Object.fromEntries(
                Object.entries(dateGrouping).reverse()
              );
              messagesListChuckTypeGroup[msgType] = dateGrouping;
            }
          });

          messagesListChuckTypeGroupRef.current = messagesListChuckTypeGroup;
          setMedialListChucked(messagesListChuckTypeGroupRef.current);
        }
      }
    }
  };

  const handleClickTab = (id: "2" | "3" | "4" | "6" | "9" | "5") => {
    setSelectedTab(id);
    clearLongPress();
  };

  const handleDelete = () => {
    getSelectedChatSession();

    if (chatSelected != null) {
      const flag: string = chatSelected?.flag!;
      let isEveryone: boolean = false;

      if (["2", "3"].includes(flag)) {
        const selectedMediaGroup: ConversationMediaListGroup[] =
          mediaListGroup.current.filter((media) =>
            mediaIdsSelected.includes(media.message_id!)
          );

        isEveryone = selectedMediaGroup.every(
          (element: ConversationMediaListGroup) => element.account_id === userId
        );
      } else {
        const selectedMedia: ConversationMediaList[] = mediaList.current.filter(
          (media) => mediaIdsSelected.includes(media.message_id!)
        );

        isEveryone = selectedMedia.every(
          (element: ConversationMediaListGroup) => element.account_id === userId
        );
      }

      dispatch(
        mediaDeleteEveryoneAction({
          isEveryone: isEveryone,
          messagesId: mediaIdsSelected,
          flag: flag,
        })
      );

      dispatch(
        addModal({
          id: ModalKeys.chat_delete_message_media,
          title:
            mediaIdsSelected.length > 1 ? "delete_messages" : "delete_message",
          titleCenter: true,
          allowBottomSheet: true,
          size: "md",
        })
      );
    }
  };

  const handleSend = () => {
    getSelectedChatSession();

    if (chatSelected != null) {
      const flag: string = chatSelected?.flag!;
      let mediaForwardList: Array<MediaForwardList> = [];

      if (["2", "3"].includes(flag)) {
        const selectedMediaGroup: ConversationMediaListGroup[] =
          mediaListGroup.current.filter((media) =>
            mediaIdsSelected.includes(media.message_id!)
          );

        selectedMediaGroup.forEach((media) => {
          mediaForwardList.push({
            file_id: media.file_id!,
            account_id: media.account_id!,
            message: media.message!,
            message_type: media.message_type!,
            decripted_mesage: media.decripted_mesage,
          });
        });
      } else {
        const selectedMedia: ConversationMediaList[] = mediaList.current.filter(
          (media) => mediaIdsSelected.includes(media.message_id!)
        );
        selectedMedia.forEach((media) => {
          mediaForwardList.push({
            file_id: media.file_id!,
            account_id: media.account_id!,
            message: media.message!,
            message_type: media.message_type!,
            decripted_mesage: media.decripted_mesage,
          });
        });
      }

      dispatch(removeModal(ModalKeys.chat_delete_message_media));
      window.history.replaceState(null, "", window.location.pathname);

      dispatch(setForwardMediaList(mediaForwardList));

      dispatch(
        addModal({
          id: ModalKeys.chat_forward_media,
          title: "forward",
          size: "lg",
          screenMobileView: type === 3 ? false : true,
        })
      );
    }
  };

  useEffect(() => {
    reset();
    getSelectedChatSession();

    if (type !== 3) {
      getMediaList();
    }
    // eslint-disable-next-line
  }, []);

  // DELETE MEDIA
  useEffect(() => {
    clearLongPress();

    if (mediaDeleteActionTypeSelector == null && type === 3) {
      getMediaList();
    }
    // eslint-disable-next-line
  }, [mediaDeleteActionTypeSelector]);

  useEffect(() => {
    clearLongPress();
  }, [forwardedMediaListSelector]);

  return (
    <ChatMediaContext.Provider
      value={{
        tabList,
        selectedTab,
        chatSelected,
        handleClickTab,
        handleDelete,
        handleSend,

        medialListChucked,

        mediaIdsSelected,
        longPressStatus,
        handleClickMedia,
        handlePressStart,
        handlePressEnd,
        clearLongPress,
      }}
    >
      {children}
    </ChatMediaContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const ChatMediaContextImport = () => {
  const context = useContext(ChatMediaContext);
  if (!context) {
    throw new Error(
      "ChatMediaContextImport must be used within a ChatMediaContextProvider"
    );
  }
  return context;
};
